import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import searchArtistSets from '../../../../api/graphql/queries/searchArtistSets';
import getSearchEvents from '../../../../api/graphql/queries/searchEvents';
import { emptyText, updateAt, removeAt, reorder } from './MainNavComponents/navEditorState';

// Typographic levels (PRI-466). Keep in sync with ps-front/src/util/lineupLevels.js.
// The order here is the order the lanes render in, top billing first.
const LEVELS = [
  { key: 'headliner', label: 'Headliner', spec: '2.5rem black' },
  { key: 'semi', label: 'Semi', spec: '1.75rem medium' },
  { key: 'medium', label: 'Medium', spec: '1.25rem medium' },
  { key: 'small', label: 'Small', spec: '1rem regular' },
];

const DEFAULT_LEVEL = 'small';

const blockTemplate = () => ({
  id: `blk_${Math.random().toString(36).slice(2, 10)}`,
  title: emptyText(),
  artists: [],
});

const laneDroppableId = (blockId, level) => `alz-lane--${blockId}--${level}`;

const parseLaneDroppableId = (droppableId) => {
  const parts = String(droppableId).split('--');
  if (parts.length !== 3 || parts[0] !== 'alz-lane') return null;
  return { blockId: parts[1], level: parts[2] };
};

// Artists of one block at one level, in the editor's own order.
const laneMembers = (block, level) => (block.artists || [])
  .map((artist, at) => ({ artist, at }))
  .filter(entry => (entry.artist.level || DEFAULT_LEVEL) === level);

const ArtistList = ({
  handlerSetComponents,
  components,
  id,
  reusableComponent,
  reusableComponentSlugname,
  languages,
  activeLanguage,
}) => {
  const initialState = {
    type: 'LineupList',
    artistSets: [],
    blocks: null,
    eventSlug: null,
    finalMessage: {
      es: '', en: '', ca: '', pt: '',
    },
    linkTo: '',
    backgroundImage: '',
    backgroundColor: '',
  };

  const eventInputRef = useRef(null);
  const searchTimer = useRef(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState();
  const [index, setIndex] = useState();
  const [state, setState] = useState(initialState);
  const [hits, setHits] = useState([]);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [overLane, setOverLane] = useState(null);

  const blocks = Array.isArray(state.blocks) ? state.blocks : null;
  const isTiered = Boolean(blocks && blocks.length);
  const readOnly = Boolean(reusableComponent);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const { component } = components[componentIndex];
    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) {
        handlerSetComponents({ component: { key: reusableComponentSlugname } }, componentIndex);
      }
      return;
    }
    setIndex(componentIndex);
    if (component) {
      setState(component);
      setSelectedEvent(component.eventSlug);
    } else {
      handlerSetComponents({ id, component: state }, componentIndex);
    }
  }, [components]);

  // Writes `next` to local state and up into the component tree.
  const commit = (next) => {
    setState(next);
    handlerSetComponents({ id, component: next }, index);
  };

  const setBlocks = nextBlocks => commit({ ...state, blocks: nextBlocks });

  const blockIndexById = blockId => (blocks || []).findIndex(b => b.id === blockId);

  // The block the picker places into: the one last focused, else the last block.
  const targetBlockIndex = () => {
    if (!blocks || !blocks.length) return -1;
    const found = blockIndexById(activeBlockId);
    return found === -1 ? blocks.length - 1 : found;
  };

  const usedSlugs = () => {
    const used = {};
    (blocks || []).forEach(b => (b.artists || []).forEach((a) => { used[a.artistSetSlugName] = true; }));
    return used;
  };

  // ---- event + artist search -------------------------------------------------

  const runArtistSearch = (value, eventSlug) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchArtistSets(value, eventSlug)
        .then(res => setHits(res || []))
        .catch((err) => { console.log(err); setHits([]); });
    }, 250);
  };

  const searchArtistsHandler = (e) => {
    const { value } = e.target;
    if (!value) return setHits([]);
    return runArtistSearch(value, selectedEvent);
  };

  const selectEventHandler = (slug) => {
    commit({ ...state, eventSlug: slug });
    setSelectedEvent(slug);
    setEvents([]);
    setHits([]);
    if (slug && eventInputRef.current) eventInputRef.current.value = '';
  };

  const searchEventHandler = (e) => {
    const { value } = e.target;
    if (!value) return setEvents([]);
    return getSearchEvents(value).then(res => setEvents(res || []));
  };

  // ---- blocks ---------------------------------------------------------------

  // Legacy flat lists keep rendering as they always have; converting is an
  // explicit action so no live page changes layout on its own.
  const convertToBlocks = () => {
    const seeded = blockTemplate();
    seeded.artists = (state.artistSets || []).map(a => ({
      artistName: a.artistName,
      artistSlugName: a.artistSlugName,
      artistSetSlugName: a.artistSetSlugName,
      level: DEFAULT_LEVEL,
    }));
    setActiveBlockId(seeded.id);
    setBlocks([seeded]);
  };

  const addBlock = () => {
    const created = blockTemplate();
    setActiveBlockId(created.id);
    setBlocks([...(blocks || []), created]);
  };

  const removeBlock = at => setBlocks(removeAt(blocks, at));

  const setBlockTitle = (at, lang, value) => {
    const block = blocks[at];
    setBlocks(updateAt(blocks, at, {
      ...block,
      title: { ...(block.title || emptyText()), [lang.toLowerCase()]: value },
    }));
  };

  // ---- artists --------------------------------------------------------------

  // C's placement grammar: pick the tier at the moment you add, so an artist is
  // never sitting around unplaced.
  const placeArtist = (artistSet, level) => {
    const at = targetBlockIndex();
    if (at === -1) return;
    if (usedSlugs()[artistSet.artistSetSlugName]) return;
    const block = blocks[at];
    setBlocks(updateAt(blocks, at, {
      ...block,
      artists: [...(block.artists || []), {
        artistName: artistSet.artistName,
        artistSlugName: artistSet.artistSlugName,
        artistSetSlugName: artistSet.artistSetSlugName,
        level,
      }],
    }));
  };

  const removeArtist = (blockAt, artistAt) => {
    const block = blocks[blockAt];
    setBlocks(updateAt(blocks, blockAt, { ...block, artists: removeAt(block.artists, artistAt) }));
  };

  // ---- drag and drop --------------------------------------------------------
  // Dragging a name into another lane is the only way to change its level, so the
  // name itself must stay non-interactive: react-beautiful-dnd refuses to start a
  // drag whose target is a button, link or input. The remove control is a real
  // button, kept outside the drag handle.

  const handleDragEnd = ({ source, destination, type }) => {
    setOverLane(null);
    if (!destination) return;

    if (type === 'ALZ-blocks') {
      if (source.index !== destination.index) {
        setBlocks(reorder(blocks, source.index, destination.index));
      }
      return;
    }

    const from = parseLaneDroppableId(source.droppableId);
    const to = parseLaneDroppableId(destination.droppableId);
    if (!from || !to) return;

    const fromBlockAt = blockIndexById(from.blockId);
    const toBlockAt = blockIndexById(to.blockId);
    if (fromBlockAt === -1 || toBlockAt === -1) return;

    // Lane indices address positions within one level, so translate them back to
    // positions in the block's flat artists array before moving anything.
    const fromLane = laneMembers(blocks[fromBlockAt], from.level);
    const moved = fromLane[source.index];
    if (!moved) return;

    const carried = { ...moved.artist, level: to.level };

    if (fromBlockAt === toBlockAt) {
      const rest = removeAt(blocks[fromBlockAt].artists, moved.at);
      const toLane = laneMembers({ artists: rest }, to.level);
      const insertAt = destination.index >= toLane.length
        ? rest.length
        : toLane[destination.index].at;
      const next = [...rest];
      next.splice(insertAt, 0, carried);
      setBlocks(updateAt(blocks, fromBlockAt, { ...blocks[fromBlockAt], artists: next }));
      return;
    }

    const sourceArtists = removeAt(blocks[fromBlockAt].artists, moved.at);
    const destArtists = [...blocks[toBlockAt].artists];
    const destLane = laneMembers(blocks[toBlockAt], to.level);
    const insertAt = destination.index >= destLane.length
      ? destArtists.length
      : destLane[destination.index].at;
    destArtists.splice(insertAt, 0, carried);

    setBlocks(updateAt(
      updateAt(blocks, fromBlockAt, { ...blocks[fromBlockAt], artists: sourceArtists }),
      toBlockAt,
      { ...blocks[toBlockAt], artists: destArtists },
    ));
  };

  const handleDragUpdate = ({ destination }) => setOverLane(destination ? destination.droppableId : null);

  // ---- render ---------------------------------------------------------------

  const renderLane = (block, blockAt, level) => {
    const members = laneMembers(block, level.key);
    const droppableId = laneDroppableId(block.id, level.key);
    return (
      <Droppable droppableId={droppableId} type="ALZ-artists" direction="horizontal" key={level.key}>
        {drop => (
          <div className={`alz-lane ${overLane === droppableId ? 'alz-lane--over' : ''}`}>
            <span className="alz-lane-tag">
              <b>{level.label}</b>
              <i>{level.spec}</i>
            </span>
            <div className="alz-lane-names" ref={drop.innerRef} {...drop.droppableProps}>
              {members.length === 0 && <span className="alz-lane-empty">empty</span>}
              {members.map((entry, i) => (
                <Draggable
                  draggableId={`alz-nm--${block.id}--${entry.artist.artistSetSlugName}`}
                  index={i}
                  key={entry.artist.artistSetSlugName}
                  isDragDisabled={readOnly}
                >
                  {drag => (
                    <span
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      className={`alz-chip ${drag.isDragging ? 'alz-chip--dragging' : ''}`}
                    >
                      {i > 0 && <span className={`alz-sep alz-t-${level.key}`}>•</span>}
                      <span
                        {...drag.dragHandleProps}
                        className={`alz-nm alz-t-${level.key}`}
                        title="Drag to another lane to change level"
                      >
                        {entry.artist.artistName || entry.artist.artistSetSlugName}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          className="alz-nm-x"
                          title={`Remove ${entry.artist.artistName || entry.artist.artistSetSlugName}`}
                          onClick={() => removeArtist(blockAt, entry.at)}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  )}
                </Draggable>
              ))}
              {drop.placeholder}
            </div>
          </div>
        )}
      </Droppable>
    );
  };

  const renderBlock = (block, blockAt) => {
    const isTarget = blockAt === targetBlockIndex();
    return (
      <Draggable draggableId={`alz-block--${block.id}`} index={blockAt} key={block.id} isDragDisabled={readOnly}>
        {drag => (
          <div ref={drag.innerRef} {...drag.draggableProps} className="alz-block">
            <div
              className={`alz-block-head ${isTarget ? 'alz-block-head--target' : ''}`}
              onClick={() => setActiveBlockId(block.id)}
              role="presentation"
              title="Click to add new artists to this block"
            >
              <span className="alz-grip" {...drag.dragHandleProps}>⠿</span>
              {languages.map(lang => (
                <input
                  key={lang}
                  id={`alz-title-${block.id}-${lang}`}
                  className="alz-block-title"
                  style={{ display: activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block' : 'none' }}
                  placeholder={`Block title — ${lang.toUpperCase()} (optional)`}
                  value={(block.title && block.title[lang.toLowerCase()]) || ''}
                  onChange={e => setBlockTitle(blockAt, lang, e.target.value)}
                  onFocus={() => setActiveBlockId(block.id)}
                  readOnly={readOnly}
                />
              ))}
              {isTarget
                ? <span className="alz-target">adding here</span>
                : <span className="alz-target-hint">add here</span>}
              <span className="alz-count">{`${(block.artists || []).length} artists`}</span>
              {!readOnly && (
                <button type="button" className="alz-x" onClick={() => removeBlock(blockAt)} title="Remove block">✕</button>
              )}
            </div>
            {LEVELS.map(level => renderLane(block, blockAt, level))}
          </div>
        )}
      </Draggable>
    );
  };

  const used = usedSlugs();

  // Name the destination where the adding happens, so it is never a guess.
  const targetBlockLabel = (() => {
    const at = targetBlockIndex();
    if (at === -1) return '';
    const title = blocks[at].title && blocks[at].title[activeLanguage.toLowerCase()];
    return title || `block ${at + 1}`;
  })();

  return (
    <div className="alz" style={{ padding: '14px 16px' }}>
      <div className="alz-row" style={{ alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: '1 1 260px', minWidth: 0, position: 'relative' }}>
          <span className="alz-lab" style={{ marginBottom: 6 }}>Event</span>
          {selectedEvent ? (
            <span className="alz-event">
              {selectedEvent}
              {!readOnly && <button type="button" className="alz-x" onClick={() => selectEventHandler(null)}>✕</button>}
            </span>
          ) : (
            <div className="alz-field">
              <span className="alz-mag">⌕</span>
              <input ref={eventInputRef} id={`alz-event-${id}`} type="text" placeholder="Search event…" onChange={searchEventHandler} readOnly={readOnly} />
            </div>
          )}
          {events.length > 0 && (
            <div className="alz-results" style={{ position: 'absolute', zIndex: 20, background: '#fff', width: '100%' }}>
              {events.map(event => (
                <button type="button" className="alz-hit" key={event.eventSlugName} onClick={() => selectEventHandler(event.eventSlugName)}>
                  <span className="alz-hit-name">{event.eventSlugName}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '2 1 320px', minWidth: 0 }}>
          <span className="alz-lab" style={{ marginBottom: 6 }}>
            {targetBlockLabel ? `Add artist to ${targetBlockLabel}` : 'Add artist'}
          </span>
          <div className={`alz-field ${!selectedEvent || !isTiered ? 'alz-field--off' : ''}`}>
            <span className="alz-mag">⌕</span>
            <input
              id={`alz-artist-${id}`}
              type="text"
              placeholder={isTiered ? 'Search artist sets in this event…' : 'Add a block first'}
              onChange={searchArtistsHandler}
              readOnly={readOnly}
            />
          </div>
          {hits.length > 0 && isTiered && (
            <div className="alz-results">
              {hits.map((hit) => {
                const already = used[hit.artistSetSlugName];
                return (
                  <div className="alz-hit" key={hit.artistSetSlugName}>
                    <span className="alz-hit-name">{hit.artistName || hit.artistSetSlugName}</span>
                    {already ? <span className="alz-hit-used">added</span> : (
                      <span className="alz-keys">
                        {LEVELS.map((level, i) => (
                          <button
                            type="button"
                            className="alz-key"
                            key={level.key}
                            title={`Place as ${level.label}`}
                            onClick={() => placeArtist(hit, level.key)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isTiered ? (
        <DragDropContext onDragEnd={handleDragEnd} onDragUpdate={handleDragUpdate}>
          <Droppable droppableId="alz-blocks" type="ALZ-blocks">
            {drop => (
              <div ref={drop.innerRef} {...drop.droppableProps}>
                {blocks.map(renderBlock)}
                {drop.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="alz-legacy">
          {(state.artistSets || []).length > 0
            ? `This list still uses the flat layout — ${state.artistSets.length} artists, all one size. Converting keeps every artist and puts them in one block where you can set levels.`
            : 'Add a block to start building. Each block is one part of the poster — a day, a stage, an announcement.'}
        </div>
      )}

      {!readOnly && (
        <div className="alz-row" style={{ gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" className="alz-add" onClick={isTiered ? addBlock : convertToBlocks}>
            {isTiered || !(state.artistSets || []).length ? '＋ Add block' : '＋ Convert to levels'}
          </button>
        </div>
      )}

      <div className="alz-foot" style={{ marginTop: 14 }}>
        {languages.map(lang => (
          <label
            key={lang}
            htmlFor={`alz-final-${id}-${lang}`}
            style={{ display: activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block' : 'none' }}
          >
            <span className="alz-lab" style={{ marginBottom: 6 }}>Final link text</span>
            <div className="alz-field">
              <input
                id={`alz-final-${id}-${lang}`}
                type="text"
                value={(state.finalMessage && state.finalMessage[lang.toLowerCase()]) || ''}
                onChange={e => commit({
                  ...state,
                  finalMessage: { ...state.finalMessage, [lang.toLowerCase()]: e.target.value },
                })}
                readOnly={readOnly}
              />
            </div>
          </label>
        ))}
        <label htmlFor={`alz-linkto-${id}`}>
          <span className="alz-lab" style={{ marginBottom: 6 }}>Final link URL</span>
          <div className="alz-field">
            <input
              id={`alz-linkto-${id}`}
              type="text"
              value={state.linkTo || ''}
              onChange={e => commit({ ...state, linkTo: e.target.value })}
              readOnly={readOnly}
            />
          </div>
        </label>
        <label htmlFor={`alz-bg-${id}`}>
          <span className="alz-lab" style={{ marginBottom: 6 }}>Background image URL</span>
          <div className="alz-field">
            <input
              id={`alz-bg-${id}`}
              type="text"
              value={state.backgroundImage || ''}
              onChange={e => commit({ ...state, backgroundImage: e.target.value })}
              readOnly={readOnly}
            />
          </div>
        </label>
        <label htmlFor={`alz-bgcolor-${id}`}>
          <span className="alz-lab" style={{ marginBottom: 6 }}>Background colour</span>
          <div className="alz-field alz-field--color">
            <input
              id={`alz-bgcolor-${id}`}
              type="color"
              className="alz-swatch"
              value={state.backgroundColor || '#ffffff'}
              onChange={e => commit({ ...state, backgroundColor: e.target.value })}
              disabled={readOnly}
            />
            <input
              id={`alz-bgcolor-hex-${id}`}
              type="text"
              placeholder="none — uses the page background"
              value={state.backgroundColor || ''}
              onChange={e => commit({ ...state, backgroundColor: e.target.value })}
              readOnly={readOnly}
            />
            {!readOnly && state.backgroundColor && (
              <button
                type="button"
                className="alz-x"
                title="Clear — use the page background"
                onClick={() => commit({ ...state, backgroundColor: '' })}
              >
                ✕
              </button>
            )}
          </div>
        </label>
      </div>
    </div>
  );
};

ArtistList.propTypes = {
  handlerSetComponents: PropTypes.func.isRequired,
  components: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    component: PropTypes.shape({ eventSlug: PropTypes.string }),
  })).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reusableComponent: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
  reusableComponentSlugname: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeLanguage: PropTypes.string.isRequired,
};

ArtistList.defaultProps = {
  reusableComponent: false,
  reusableComponentSlugname: '',
};

export default ArtistList;
